/**
 * "AI Screen" tab — the verdict the rules produced (word, score, reasons),
 * the attempts timeline, the answers table and the two actions. The copy
 * keeps the brief's principle in view: code decides qualification, a person
 * makes the hiring decision.
 */
import { useId, useState } from 'react';
import { Link } from 'react-router';
import { Loader2, PhoneOutgoing, RotateCcw } from 'lucide-react';
import { Modal } from '@/components/Modal';
import { errorMessage, useToast } from '@/components/Toast';
import { hasMinRole, useAuth } from '@/lib/auth';
import { fullName, jsonObject, type LeadDetail } from '@/features/pipeline/types';
import { screeningFields } from '@/features/screening/normalize';
import { maxScore } from '@/features/screening/qualify';
import { humanizeReasons, leadReasons } from '@/features/screening/reasons';
import { qualificationResultMeta } from '@/features/screening/screening-format';
import { useLeadScreening, useScreeningActions, useScreeningConfigs } from '@/features/screening/screening-api';
import { isActiveScreening, type ScreeningCall } from '@/features/screening/types';
import { ScreeningAnswers } from './ScreeningAnswers';
import { ScreeningAttempt } from './ScreeningAttempt';

const btn = 'inline-flex min-h-10 items-center gap-1.5 rounded-md px-3 text-[13px] font-medium disabled:opacity-50';

function lastAnswered(attempts: ScreeningCall[]): ScreeningCall | null {
  for (let i = attempts.length - 1; i >= 0; i--) {
    const c = attempts[i]!;
    if (c.status === 'completed' && c.extracted_json) return c;
  }
  return null;
}

export function LeadScreeningTab({ lead }: { lead: LeadDetail }) {
  const id = useId();
  const toast = useToast();
  const { user } = useAuth();
  const isAdmin = hasMinRole(user, 'company_admin');
  const screeningQ = useLeadScreening(lead.id);
  const configsQ = useScreeningConfigs();
  const { call, requalify } = useScreeningActions(lead.id);
  const [confirmCall, setConfirmCall] = useState(false);

  const fields = screeningFields(lead);
  const config = configsQ.data?.[lead.role.slug] ?? null;
  const questions = config?.questions ?? [];
  const attempts = screeningQ.data?.attempts ?? [];
  const latest = screeningQ.data?.latest ?? null;
  const answered = lastAnswered(attempts);
  const answers = answered ? jsonObject(answered.extracted_json) : fields?.answers ?? {};
  const hasAnswers = Object.keys(answers).length > 0;

  const result = fields?.result ?? lead.qualification;
  const meta = qualificationResultMeta(result);
  // The stored `max_score` is what the worker actually scored against; the
  // config total is only a fallback for rows written before it was stored.
  const max = fields?.max_score ?? (config ? maxScore(config) : null);
  // Plain English for the recruiter; the worker's shorthand stays untouched in the API.
  const reasons = humanizeReasons(leadReasons(lead), config);
  const active = isActiveScreening(latest?.status);
  // The worker's `config_draft` is authoritative (the settings read may be
  // stale or forbidden for non-admins).
  const draft = screeningQ.data?.config_draft === true;

  const onCall = () => {
    setConfirmCall(false);
    call.mutate(undefined, {
      onSuccess: () => toast.push({ kind: 'success', title: 'Call scheduled', message: `${fullName(lead.candidate)} will be phoned shortly.` }),
      onError: (err) => toast.push({ kind: 'error', title: 'Could not start the call', message: errorMessage(err) }),
    });
  };
  const onRequalify = () =>
    requalify.mutate(undefined, {
      onSuccess: () => toast.push({ kind: 'success', title: 'Qualification re-run', message: 'The result below reflects the current rules.' }),
      onError: (err) => toast.push({ kind: 'error', title: 'Could not re-run qualification', message: errorMessage(err) }),
    });

  return (
    <div className="space-y-5" data-testid="screening-tab">
      {/* rounded-lg like every other card nested inside the detail panel (Resume, Assessment, Try-it). */}
      <section aria-labelledby={`${id}-verdict`} className={`rounded-lg border-2 bg-white px-4 py-3.5 ${meta.ring}`}>
        <h3 id={`${id}-verdict`} className="sr-only">Qualification result</h3>
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <span className={`inline-flex items-center gap-2 text-xl font-semibold tracking-tight ${meta.text}`} data-testid="screening-result">
            <span className={`h-2.5 w-2.5 rounded-full ${meta.dot}`} aria-hidden />
            {meta.label}
          </span>
          {fields?.score !== null && fields?.score !== undefined && (
            <span className="text-[13px] text-gray-700 tabular-nums" data-testid="screening-score">
              Score {fields.score}{max !== null && max > 0 ? ` / ${max}` : ''}
            </span>
          )}
        </div>
        <p className="mt-0.5 text-[13px] text-muted">{meta.hint}</p>
        {reasons.length > 0 && (
          <ul className="mt-2.5 list-disc space-y-0.5 pl-5 text-[13px] text-gray-800" aria-label="Why">
            {reasons.map((r, i) => <li key={i}>{r}</li>)}
          </ul>
        )}
        <p className="mt-3 border-t border-gray-100 pt-2 text-[12px] text-muted">
          The rules for {lead.role.title} decide this result; final hiring decisions are made by a person.
        </p>
      </section>

      {draft && (
        <div role="note" className="flex items-start gap-2.5 rounded-lg border border-warning/30 bg-warning-bg px-3 py-2.5 text-[13px] text-gray-900" data-testid="draft-notice">
          <div>
            <span className="font-medium text-warning-text">Draft rules.</span> The questions and rules for {lead.role.title} have not been reviewed yet, so results are provisional.{' '}
            {isAdmin ? (
              <Link to="/settings/screening" className="font-medium text-navy underline-offset-2 hover:underline">Review them in Settings → Screening</Link>
            ) : (
              <span>A company admin can review them in Settings → Screening.</span>
            )}
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <button type="button" onClick={() => setConfirmCall(true)} disabled={call.isPending || active} className={`${btn} bg-navy text-white hover:bg-navy-dark`}>
          {call.isPending ? <Loader2 size={14} className="animate-spin" aria-hidden /> : <PhoneOutgoing size={14} strokeWidth={2} aria-hidden />}
          {active ? 'Call in progress' : 'Call now'}
        </button>
        <button type="button" onClick={onRequalify} disabled={requalify.isPending || !hasAnswers} className={`${btn} border border-gray-200 bg-white text-gray-800 hover:bg-page`}>
          {requalify.isPending ? <Loader2 size={14} className="animate-spin" aria-hidden /> : <RotateCcw size={14} strokeWidth={2} aria-hidden />}
          Re-run qualification
        </button>
      </div>

      <section aria-labelledby={`${id}-attempts`}>
        <h3 id={`${id}-attempts`} className="mb-2 text-[13px] font-semibold text-gray-900">Calls</h3>
        {screeningQ.isPending ? (
          <p className="text-[13px] text-muted" aria-busy="true">Loading calls…</p>
        ) : screeningQ.isError ? (
          <p role="alert" className="text-[13px] text-danger">Could not load the calls: {errorMessage(screeningQ.error)}</p>
        ) : attempts.length === 0 ? (
          <p className="text-[13px] text-muted">No call has been placed yet. “Call now” phones the candidate and asks the {lead.role.title} questions.</p>
        ) : (
          <ol className="space-y-4 border-l-2 border-gray-200 pl-0 [&>li]:ml-[-9px]" aria-live={active ? 'polite' : undefined}>
            {attempts.map((c) => <ScreeningAttempt key={c.id} call={c} isLatest={c.id === latest?.id} />)}
          </ol>
        )}
      </section>

      <section aria-labelledby={`${id}-answers`}>
        <h3 id={`${id}-answers`} className="mb-2 text-[13px] font-semibold text-gray-900">Answers</h3>
        <ScreeningAnswers questions={questions} answers={answers} flagMissing={hasAnswers} />
      </section>

      <Modal
        open={confirmCall}
        title={`Call ${fullName(lead.candidate)} now?`}
        description={`The recruiting assistant will phone ${lead.candidate.phone_e164 || 'the candidate'} right away and ask the ${lead.role.title} screening questions. The candidate will hear the call is from YES! Automotive.`}
        onClose={() => setConfirmCall(false)}
        footer={
          <>
            <button type="button" onClick={() => setConfirmCall(false)} className={`${btn} border border-gray-200 bg-white text-gray-800 hover:bg-page`}>Cancel</button>
            <button type="button" onClick={onCall} className={`${btn} bg-navy text-white hover:bg-navy-dark`}>Place the call</button>
          </>
        }
      />
    </div>
  );
}
